import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resourceApi, type AutomationDef, type EmailResource, type Step, type TemplateDef, type MessageSpec } from '../../lib/email-resources-api';
import { emailKey } from '../../lib/email-api';
import { Icon } from '../ui/Icon';
import styles from './EmailAuthoring.module.css';

const exampleEmails = [
  { mode: 'exact', title: 'Send your proposal', subject: 'Your project proposal', body: 'Hi Alex,\n\nHere’s the proposal we discussed. Let me know what you think—I’m happy to walk through it together.' },
  { mode: 'guidance', title: 'Personalized follow-up', subject: 'Help them take the next step', body: 'Use our conversation to ask about the proposal. Offer help with their priorities. Keep it warm, brief, and low pressure.' },
];

export function SequenceIntroduction({ onCreate }: { onCreate: () => void }) {
  const titleId = useId();
  const descriptionId = useId();
  const actionId = useId();
  const arrowId = useId();
  return <section className={styles.sequenceIntroduction} aria-labelledby={titleId}>
    <div className={styles.introFlow} aria-hidden="true">
      <svg className={styles.introConnections} viewBox="0 0 920 420" fill="none">
        <defs><marker id={arrowId} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="m2 1 3 3-3 3" /></marker></defs>
        <path d="M240 290 C285 290 285 240 330 240" markerEnd={`url(#${arrowId})`} />
        <path d="M500 240 C575 240 575 110 650 110" markerEnd={`url(#${arrowId})`} />
        <path d="M500 240 C575 240 575 362 650 362" markerEnd={`url(#${arrowId})`} />
      </svg>
      <div className={styles.introFirstEmail}><SequenceExampleTemplate email={exampleEmails[0]!} number={1} /></div>
      <div className={styles.introDecision}><span>3 days after sending</span><strong>Did they reply?</strong></div>
      <span className={styles.introNoReply}>No reply</span>
      <div className={styles.introFollowUp}><SequenceExampleTemplate email={exampleEmails[1]!} number={2} /></div>
      <span className={styles.introReplied}>They replied</span>
      <div className={styles.introStop}><span>✓</span> Stop follow-ups</div>
    </div>
    <div className={styles.sequencePitch}>
      <h3 id={titleId}>Follow up without<br />keeping it all in your head.</h3>
      <p id={descriptionId}>A sequence is your reusable email plan: what to say, when to follow up, and when to stop. Use your wording or let Echo write it. Each contact’s next email arrives in Home, ready for you to review and send.</p>
      <span id={actionId} className={styles.introCreateLabel}>Create my first sequence <span aria-hidden="true">→</span></span>
    </div>
    <button type="button" className={styles.sequenceCreateOverlay} onClick={onCreate} aria-labelledby={actionId} aria-describedby={descriptionId} />
  </section>;
}

function SequenceExampleTemplate({ email, number }: { email: typeof exampleEmails[number]; number: number }) {
  return <article className={`${styles.templateCard} ${styles.introTemplate}`}>
    <span className={styles.templateCardTop}><Icon name={email.mode === 'guidance' ? 'sparkles' : 'mail'} size={18} /><span>{email.mode === 'guidance' ? 'AI guidance' : 'Your wording'}</span></span>
    <h3>{email.title}</h3>
    <div className={styles.templateSnippet}><strong>{email.subject}</strong><p>{email.body.replace(/^Hi Alex,\n\n/, '')}</p></div>
    <span className={styles.templateCardFooter}>Email {number} · {email.mode === 'guidance' ? 'Echo writes a fresh draft' : 'Your words, ready to reuse'}</span>
  </article>;
}

export function isEmptySequenceStep(step: Step) {
  if (!('inline' in step.message)) return false;
  const message = step.message.inline;
  return message.mode === 'guidance'
    ? !message.purpose.trim() && !message.instructions.trim()
    : ![...message.subject, ...message.body].some(segment => segment.kind !== 'literal' || segment.text.trim());
}

export function SequenceRow({ resource, children }: { resource: EmailResource; children: ReactNode }) {
  const definition = resource.definition as AutomationDef;
  const counts = resource.counts;
  const count = (...states: string[]) => counts?.filter(c => states.includes(c.state)).reduce((sum, c) => sum + c.count, 0) ?? 0;
  const enrolled = counts?.filter(c => !['completed', 'stopped'].includes(c.state)).reduce((sum, c) => sum + c.count, 0) ?? 0;
  const metrics = [
    { label: 'Enrolled now', value: enrolled, hint: 'All unfinished enrollments, including paused and blocked records.' },
    { label: 'To review', value: count('awaiting_review'), hint: 'Enrollments with an email awaiting your review.' },
    { label: 'Needs attention', value: count('blocked', 'needs_input'), hint: 'Enrollments blocked or needing input.' },
    { label: 'Completed', value: count('completed'), hint: 'Reached the end of the email plan. This does not measure replies or goal success.' },
  ];
  return <article className={styles.sequenceRow}>
    <a className={styles.sequenceRowLink} href={`/app/email/automations/${resource.id}${window.location.search}`}>
      <div className={styles.sequenceRowName}><strong>{resource.name}</strong><span className={styles.sequenceStatus} data-state={resource.state}>{resource.state ?? 'draft'}</span>
        <small>{definition.steps.length} {definition.steps.length === 1 ? 'email' : 'emails'} · {definition.trigger.kind === 'manual' ? 'Contacts you choose' : definition.trigger.kind === 'created' ? 'New records' : 'Record changes'} · {resource.published_version_id ? 'Published' : 'Working copy'}</small>
      </div>
      <dl className={styles.sequenceMetrics}>{metrics.map(metric => <div key={metric.label} title={metric.hint}><dt>{metric.label}</dt><dd>{counts ? metric.value : '—'}</dd></div>)}</dl>
      <span aria-hidden="true">→</span>
    </a>
    {children}
  </article>;
}

function stepSummary(step: Step, templates: EmailResource[]) {
  if ('inline' in step.message) {
    const message = step.message.inline;
    return {
      mode: message.mode === 'guidance' ? 'Echo writes each draft' : 'Your wording',
      title: message.mode === 'guidance' ? message.purpose || 'Describe this email' : message.subject.map(s => s.kind === 'literal' ? s.text : '[Contact detail]').join('') || (step.threadMode === 'reply_previous' ? 'Reply in the same conversation' : 'Write your first email'),
      body: message.mode === 'guidance' ? message.instructions : message.body.map(s => s.kind === 'literal' ? s.text : '[Contact detail]').join(''),
    };
  }
  const versionId = step.message.templateVersionId;
  return { mode: 'Saved template', title: templates.find(t => t.published_version_id === versionId)?.name ?? 'Selected template version', body: 'Uses the saved version selected for this step.' };
}

export function SequenceStep({ step, index, templates, autoOpen = false, empty = false, onChange, onRemove, children }: {
  step: Step;
  index: number;
  templates: EmailResource[];
  autoOpen?: boolean;
  empty?: boolean;
  onChange: (step: Step) => void;
  onRemove?: (() => void) | undefined;
  children: (draft: Step, update: (step: Step) => void) => ReactNode;
}) {
  const [open, setOpen] = useState(autoOpen);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => { if (autoOpen) card.current?.scrollIntoView({ block: 'nearest', inline: 'center' }); }, [autoOpen]);
  const pinned = useSequenceTemplate(step, templates);
  const summary = stepSummary(pinned.message ? { ...step, message: { inline: pinned.message } } : step, templates);
  const delay = step.afterPreviousSend ?? { amount: 3, unit: 'business_days' };
  return <li className={styles.builderStep}>
    {!empty && (index > 0 ? <div className={styles.builderBranch}>
      <svg viewBox="0 0 200 340" fill="none" aria-hidden="true"><path d="M0 150 C10 150 10 150 20 150 M160 150 C180 150 180 150 200 150 M160 150 C202 150 192 297 170 297" /><path d="m194 146 5 4-5 4 M176 293 l-5 4 5 4" /></svg>
      <button type="button" className={styles.builderWait} onClick={() => setOpen(true)} aria-label={`Edit wait before email ${index + 1}`}><small>After sending</small><strong>Wait {delay.amount} {delay.unit.replaceAll('_', ' ')}</strong><span>If no reply →</span></button>
      <span className={styles.builderReplyLabel}>They reply</span>
      <span className={styles.builderStop}>✓ Stop follow-ups</span>
    </div> : <span className={styles.builderConnector} aria-hidden="true">→</span>)}
    <div className={styles.builderEmail} ref={card}>
      {empty ? <button type="button" className={styles.addEmailCard} onClick={() => setOpen(true)}><span aria-hidden="true">＋</span><strong>Add email</strong><small>Choose a template or write with Echo</small></button> : <button type="button" className={`${styles.templateCard} ${styles.builderEmailCard}`} onClick={() => setOpen(true)} aria-label={`Edit email ${index + 1}: ${summary.title}`}>
        <span className={styles.templateCardTop}><Icon name={summary.mode === 'Echo writes each draft' ? 'sparkles' : 'mail'} size={18} /><span>{summary.mode === 'Echo writes each draft' ? 'AI guidance' : summary.mode}</span></span>
        <h3>{pinned.template?.name ?? summary.title}</h3>
        <span className={styles.builderEmailBody}>{summary.body || 'Choose a saved template, write your email, or give Echo instructions.'}</span>
        <span className={styles.templateCardFooter}>Email {index + 1}<span>Edit email ↗</span></span>
      </button>
      }
      {onRemove && <button type="button" className={styles.removeFlowStep} onClick={onRemove}>Remove follow-up {index}</button>}
    </div>
    {empty && <div className={styles.builderExampleBranch} aria-label="Example branch: if they reply, stop follow-ups">
      <svg viewBox="0 0 120 180" fill="none" aria-hidden="true"><path d="M0 90 C60 90 60 150 116 150" /><path d="m110 145 6 5-6 5" /></svg>
      <div><small>If they reply</small><span>✓ Stop follow-ups</span></div>
    </div>}
    {open && <StepDialog step={step} index={index} onClose={() => setOpen(false)} onChange={onChange}>{children}</StepDialog>}
  </li>;
}

function useSequenceTemplate(step: Step, templates: EmailResource[]) {
  const versionId = 'templateVersionId' in step.message ? step.message.templateVersionId : undefined;
  const template = templates.find(t => t.published_version_id === versionId || t.versions?.some(v => v.id === versionId));
  const query = useQuery({ queryKey: [...emailKey(), 'templates', template?.id], queryFn: () => resourceApi.detail('templates', template!.id), enabled: !!versionId && !!template });
  const version = query.data?.versions?.find(v => v.id === versionId);
  return { template, query, message: version ? (version.definition as TemplateDef).message : undefined };
}

export function SequenceTemplateChoice({ step, templates, onCustomize }: { step: Step; templates: EmailResource[]; onCustomize: (message: MessageSpec) => void }) {
  const { template, query, message } = useSequenceTemplate(step, templates);
  return <div className={styles.selectedTemplate}>
    <strong>{template?.name ?? 'Previously selected template'}</strong>
    {query.isFetching && <p role="status">Loading saved email…</p>}
    {query.isError && <p role="alert">Couldn’t load the template. <button type="button" onClick={() => void query.refetch()}>Try again</button></p>}
    {message && <>
      <p>{message.mode === 'guidance' ? message.instructions : message.body.map(s => s.kind === 'literal' ? s.text : '[Contact detail]').join('')}</p>
      <button type="button" onClick={() => onCustomize(message)}>Customize for this sequence</button>
    </>}
    <p className={styles.hint}>Uses the saved version you selected. Customizing creates a copy for this email; your library template stays the same.</p>
  </div>;
}

export function SequenceSettings({ label, summary, children }: { label: string; summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className={styles.builderStart} onClick={() => setOpen(true)}>
      <small>Start</small><strong>{label}</strong><span>{summary}</span><small>Edit audience & trigger ↗</small>
    </button>
    {open && <SequenceSettingsDialog onClose={() => setOpen(false)}>{children}</SequenceSettingsDialog>}
  </>;
}

function SequenceSettingsDialog({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => { if (element?.open) element.close(); };
  }, []);
  return <dialog ref={dialog} className={styles.templateModal} aria-labelledby={title} onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className={styles.modalHeading}><h2 id={title}>Who enters this sequence?</h2><button type="button" aria-label="Close audience settings" onClick={onClose}>×</button></div>
    <p className={styles.hint}>Choose your audience and how the sequence starts. Changes update your working copy; save the sequence when you’re ready.</p>
    {children}
    <div className={styles.modalActions}><button type="button" className={styles.primary} onClick={onClose}>Done</button></div>
  </dialog>;
}

function StepDialog({ step, index, onClose, onChange, children }: {
  step: Step; index: number; onClose: () => void; onChange: (step: Step) => void;
  children: (draft: Step, update: (step: Step) => void) => ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const description = useId();
  const [draft, setDraft] = useState(step);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => { if (element?.open) element.close(); };
  }, []);
  return <dialog ref={dialog} className={styles.templateModal} aria-labelledby={title} aria-describedby={description} onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className={styles.modalHeading}><h2 id={title}>Edit email {index + 1}</h2><button type="button" aria-label="Cancel email edits" onClick={onClose}>×</button></div>
    <p id={description} className={styles.hint}>Apply changes to your plan, then save the sequence. Existing enrollments keep their published version.</p>
    {children(draft, setDraft)}
    <div className={styles.modalActions}><button type="button" onClick={onClose}>Cancel</button><button type="button" className={styles.primary} onClick={() => { onChange(draft); onClose(); }}>Apply to sequence</button></div>
  </dialog>;
}
